import { api } from './api';
import { Icd10Code, GroupedIcd10Code, ProcedureCatalogItem, Judet, Localitate, HospitalOption } from '@/types/reference.types';

// Cache for ICD-10 MCC codes
let icd10CodesCache: Icd10Code[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Cache for genetic condition codes (icd10_genetic_codes)
let geneticConditionsCache: GroupedIcd10Code[] | null = null;
let geneticConditionsCacheTimestamp: number | null = null;

// Cache for congenital malformation codes (icd10_malformation_codes)
let malformationCodesCache: GroupedIcd10Code[] | null = null;
let malformationCodesCacheTimestamp: number | null = null;

// Cache for procedure catalog (procedure_catalog)
let procedureCatalogCache: ProcedureCatalogItem[] | null = null;
let procedureCatalogCacheTimestamp: number | null = null;

// Cache for judete
let judeteCache: Judet[] | null = null;
let judeteCacheTimestamp: number | null = null;

// Cache for localitati (keyed by id_judet, 0 for all)
const localitatiCache: Map<number, Localitate[]> = new Map();
const localitatiCacheTimestamps: Map<number, number> = new Map();

class ReferenceService {
  async getHospitals(): Promise<HospitalOption[]> {
    try {
      const response = await api.get<HospitalOption[]>('/api/reference/hospitals');
      return response.data || [];
    } catch (error) {
      console.error('Failed to load hospitals:', error);
      return [];
    }
  }

  /**
   * Fetch all ICD-10 MCC codes from the reference endpoint
   * Results are cached for 10 minutes to reduce API calls
   */
  async getIcd10ChdCodes(): Promise<Icd10Code[]> {
    const now = Date.now();

    // Check cache validity
    if (icd10CodesCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
      return icd10CodesCache;
    }

    try {
      const response = await api.get<any>('/api/reference/icd10-chd-codes');

      // Update cache
      icd10CodesCache = response.data || [];
      cacheTimestamp = now;

      return icd10CodesCache || [];
    } catch (error) {
      console.error('Failed to load ICD-10 MCC codes:', error);

      // Return cached data if available, otherwise empty array
      return icd10CodesCache || [];
    }
  }

  /**
   * Force refresh the ICD-10 codes cache
   */
  async refreshIcd10Cache(): Promise<Icd10Code[]> {
    this.clearIcd10Cache();
    return this.getIcd10ChdCodes();
  }

  /**
   * Clear the ICD-10 codes cache
   */
  clearIcd10Cache(): void {
    icd10CodesCache = null;
    cacheTimestamp = null;
  }

  /**
   * Get a single ICD-10 code by code string
   */
  async getIcd10CodeByCode(code: string): Promise<Icd10Code | null> {
    const codes = await this.getIcd10ChdCodes();
    return codes.find(c => c.icd10_code === code) || null;
  }

  /**
   * Format ICD-10 code for display
   */
  formatIcd10Code(code: Icd10Code): string {
    return `${code.icd10_code} - ${code.description_ro}`;
  }

  /**
   * Fetch all genetic condition ICD-10-AM codes (Q90-Q99)
   * Results are cached for 10 minutes to reduce API calls
   */
  async getGeneticConditions(): Promise<GroupedIcd10Code[]> {
    const now = Date.now();

    if (geneticConditionsCache && geneticConditionsCacheTimestamp && (now - geneticConditionsCacheTimestamp < CACHE_DURATION)) {
      return geneticConditionsCache;
    }

    try {
      const response = await api.get<any>('/api/reference/genetic-conditions');

      geneticConditionsCache = response.data || [];
      geneticConditionsCacheTimestamp = now;

      return geneticConditionsCache || [];
    } catch (error) {
      console.error('Failed to load genetic condition codes:', error);
      return geneticConditionsCache || [];
    }
  }

  /**
   * Fetch all congenital malformation ICD-10 codes (Q00-Q28, all body systems)
   * Results are cached for 10 minutes to reduce API calls
   */
  async getMalformationCodes(): Promise<GroupedIcd10Code[]> {
    const now = Date.now();

    if (malformationCodesCache && malformationCodesCacheTimestamp && (now - malformationCodesCacheTimestamp < CACHE_DURATION)) {
      return malformationCodesCache;
    }

    try {
      const response = await api.get<any>('/api/reference/icd10-malformation-codes');

      malformationCodesCache = response.data || [];
      malformationCodesCacheTimestamp = now;

      return malformationCodesCache || [];
    } catch (error) {
      console.error('Failed to load malformation codes:', error);
      return malformationCodesCache || [];
    }
  }

  /**
   * Fetch the curated procedure catalog (CHD surgical + interventional procedures)
   * Results are cached for 10 minutes to reduce API calls
   */
  async getProcedureCatalog(): Promise<ProcedureCatalogItem[]> {
    const now = Date.now();

    if (procedureCatalogCache && procedureCatalogCacheTimestamp && (now - procedureCatalogCacheTimestamp < CACHE_DURATION)) {
      return procedureCatalogCache;
    }

    try {
      const response = await api.get<any>('/api/reference/procedure-catalog');

      procedureCatalogCache = response.data || [];
      procedureCatalogCacheTimestamp = now;

      return procedureCatalogCache || [];
    } catch (error) {
      console.error('Failed to load procedure catalog:', error);
      return procedureCatalogCache || [];
    }
  }

  /**
   * Fetch all judete (Romanian counties)
   * Results are cached for 10 minutes
   */
  async getJudete(): Promise<Judet[]> {
    const now = Date.now();

    // Check cache validity
    if (judeteCache && judeteCacheTimestamp && (now - judeteCacheTimestamp < CACHE_DURATION)) {
      return judeteCache;
    }

    try {
      const response = await api.get<any>('/api/reference/judete');

      // Update cache
      judeteCache = response.data || [];
      judeteCacheTimestamp = now;

      return judeteCache || [];
    } catch (error) {
      console.error('Failed to load judete:', error);
      return judeteCache || [];
    }
  }

  /**
   * Fetch localitati (localities) filtered by judet
   * @param idJudet - Optional judet ID to filter by. If null/undefined, fetches all.
   * @param search - Optional search term for locality name
   * Results are cached for 10 minutes per judet
   */
  async getLocalitati(idJudet?: number | null, search?: string): Promise<Localitate[]> {
    const now = Date.now();
    const cacheKey = idJudet || 0; // Use 0 for all localities

    // Skip cache if search is provided
    if (!search) {
      const cachedData = localitatiCache.get(cacheKey);
      const cachedTimestamp = localitatiCacheTimestamps.get(cacheKey);

      if (cachedData && cachedTimestamp && (now - cachedTimestamp < CACHE_DURATION)) {
        return cachedData;
      }
    }

    try {
      const params: any = {};
      if (idJudet) {
        params.id_judet = idJudet;
      }
      if (search) {
        params.search = search;
      }

      const response = await api.get<any>('/api/reference/localitati', { params });

      const data = response.data || [];

      // Update cache only if no search term
      if (!search) {
        localitatiCache.set(cacheKey, data);
        localitatiCacheTimestamps.set(cacheKey, now);
      }

      return data;
    } catch (error) {
      console.error('Failed to load localitati:', error);

      // Return cached data if available and no search
      if (!search) {
        return localitatiCache.get(cacheKey) || [];
      }
      return [];
    }
  }

  /**
   * Clear judete cache
   */
  clearJudeteCache(): void {
    judeteCache = null;
    judeteCacheTimestamp = null;
  }

  /**
   * Clear localitati cache
   */
  clearLocalitatiCache(): void {
    localitatiCache.clear();
    localitatiCacheTimestamps.clear();
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    this.clearIcd10Cache();
    this.clearJudeteCache();
    this.clearLocalitatiCache();
    geneticConditionsCache = null;
    geneticConditionsCacheTimestamp = null;
    malformationCodesCache = null;
    malformationCodesCacheTimestamp = null;
    procedureCatalogCache = null;
    procedureCatalogCacheTimestamp = null;
  }
}

export const referenceService = new ReferenceService();
